import { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stepper,
  Step,
  StepLabel,
  Button,
  IconButton,
  TextField,
  MenuItem,
  Grid,
  Box,
  Typography,
  Autocomplete,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import { useGeolocated } from "react-geolocated";
import Swal from "sweetalert2";

const STEPS = [
  "Firm & Address",
  "Proprietor",
  "Infrastructure",
  "Bank / Security",
  "Turnover & Products",
  "Product of Interest",
  "Transport & Dealer",
  "Documents",
];

const YES_NO = ["Yes", "No"];

// Which form keys / arrays belong to each backend page (1-8). Used to detect
// whether a page was actually modified before calling the page-wise API.
const PAGE_FIELDS = {
  1: {
    scalars: [
      "Firm_Name__c",
      "Address__c",
      "address_2__c",
      "address_3__c",
      "State__c",
      "Other_State__c",
      "District__c",
      "Other_District__c",
      "Sub_District__c",
      "Other_Sub_District__c",
      "Village__c",
      "Other_Village__c",
      "City__c",
      "Pincode__c",
      "Telephone__c",
      "Mobile__c",
      "Is_Mobile_Linked__c",
      "Is_Mobile_Whatsapp__c",
      "Alternate_Mobile__c",
      "Email__c",
      "Establishment_Year__c",
      "Nature_of_Firms__c",
      "other_nature_of_Firms__c",
      "Business_Type__c",
      "Geo_Location__Latitude__s",
      "Geo_Location__Longitude__s",
    ],
    arrays: [],
  },
  2: { scalars: [], arrays: ["Proprietor_Details__c"] },
  3: {
    scalars: [
      "Shop_Office__c",
      "Godown_Area__c",
      "Godown_Facility__c",
      "Total_Employees__c",
      "Total_Vehicles__c",
      "GSTIN_applied__c",
      "GSTIN_no__c",
      "GSTIN_Registration_Date__c",
      "INST_LIC_NO__c",
      "INST_LIC_Registration_Date__c",
      "INST_LIC_Registration_Validity__c",
      "FERT_LIC_NO__c",
      "FERT_LIC_Registration_Date__c",
      "FERT_LIC_Registration_Validity__c",
    ],
    arrays: [],
  },
  4: {
    scalars: [
      "DD_Cheque_Online_Ref_No__c",
      "Date__c",
      "Bank_Name__c",
      "Amount__c",
      "Bankers_Name__c",
      "Bankers_Address__c",
      "Cash_Credit_Limit__c",
      "Deposit_Others__c",
    ],
    arrays: [],
  },
  5: {
    scalars: [
      "Pesticides__c",
      "Fertilizers__c",
      "Seeds__c",
      "Product_Others__c",
      "Is_Income_Tax_Assessee",
      "PAN_No__c",
      "Total_Annual_Turnover__c",
      "Capital_Employed__c",
      "Annual_Income__c",
      "Expected_IIL_Investment__c",
      "Expected_1_Year_Turnover_With_IIL_c",
      "Expected_Next_Year_Turnover_With_IIL_c",
      "Proposed_Payment_Terms",
      "Payment_Credit_Limit__c",
      "Total_Retailers__c",
      "Total_Villages_Covered__c",
      "Area_Specification__c",
    ],
    arrays: [
      "Total_Districts_Covered__c",
      "Company_Details__c",
      "Major_Crops__c",
    ],
  },
  6: {
    scalars: [],
    arrays: ["Product_Of_Interest__c", "Sister_Company_Details__c"],
  },
  7: {
    scalars: [
      "Dealer_Credit__c",
      "Dealer_Credit_Days__c",
      "Transporter_Name_1__c",
      "Transporter_Name_2__c",
      "Transporter_Name_3__c",
      "Depot_Distance__c",
      "Other_Dealer_Info__c",
      "Is_Party_Visited_Personally__c",
      "Party_Credibility__c",
    ],
    arrays: ["Material_Dispatch_Destination__c"],
  },
  8: { scalars: [], arrays: ["Files__c"] },
};

// Extra keys the backend reads for a page but which aren't user-edited fields
// (hidden pass-throughs and computed totals). Sent along with the page's data.
const PAGE_EXTRA_SEND = {
  1: [
    "z1_user__c",
    "z2_user__c",
    "z3_user__c",
    "z4_user__c",
    "z5_user__c",
    "z6_user__c",
    "z7_user__c",
    "z8_user__c",
  ],
  4: ["Deposit_Total__c"],
  5: ["Product_Total__c"],
};

// ---- helpers -------------------------------------------------------------

const optId = (o) => o?.sfid ?? o?.id ?? o?.__id ?? o?.value ?? "";

const optLabel = (o) =>
  o?.name__c ??
  o?.name ??
  o?.label ??
  o?.__label ??
  o?.state_name__c ??
  o?.district_name__c ??
  o?.sub_district_name__c ??
  o?.village_name__c ??
  "";

const num = (v) => {
  if (v === "" || v === null || v === undefined) return 0;
  const n = Number(v);
  return isNaN(n) ? 0 : n;
};

const toDate = (v) => (v ? dayjs(v) : null);
const fromDate = (d) => (d ? dayjs(d).format("YYYY-MM-DD") : null);

// Classify a document by its URL extension (falling back to the field name),
// so the Documents step can inline-preview images and PDFs. Query strings on
// signed URLs are stripped before checking the extension.
const fileKind = (url = "", name = "") => {
  const clean = String(url).split("?")[0].split("#")[0].toLowerCase();
  const hint = `${clean} ${String(name).toLowerCase()}`;
  if (/\.(png|jpe?g|gif|webp|bmp|svg)$/.test(clean) || /image/.test(hint)) {
    return "image";
  }
  if (/\.pdf$/.test(clean) || /pdf/.test(hint)) return "pdf";
  return "other";
};

const notify = (msg, isError) => {
  if (window?.NotificationUtils) {
    isError
      ? window.NotificationUtils.showError(msg)
      : window.NotificationUtils.showSuccess(msg);
  } else if (isError) {
    console.error(msg);
  }
};

// Map the detail record (snake_case) -> wizard form (PascalCase submit keys)
const mapRecordToForm = (d = {}) => ({
  // page 1
  Firm_Name__c: d.firm_name__c ?? "",
  Address__c: d.address__c ?? "",
  address_2__c: d.address_2__c ?? "",
  address_3__c: d.address_3__c ?? "",
  State__c: d.state__c ?? "",
  Other_State__c: d.other_state__c ?? "",
  District__c: d.district__c ?? "",
  Other_District__c: d.other_district__c ?? "",
  Sub_District__c: d.sub_district__c ?? "",
  Other_Sub_District__c: d.others_sub_district__c ?? "",
  Village__c: d.village__c ?? "",
  Other_Village__c: d.other_village__c ?? "",
  City__c: d.city__c ?? "",
  Pincode__c: d.pincode__c ?? "",
  Telephone__c: d.telephone__c ?? "",
  Mobile__c: d.mobile__c ?? "",
  Is_Mobile_Linked__c: d.is_this_mobile_number_linked_with_aadhaa__c ?? "",
  Is_Mobile_Whatsapp__c: d.is_this_a_whatsapp_number__c ?? "",
  Alternate_Mobile__c: d.alternate_mobile__c ?? "",
  Email__c: d.email__c ?? "",
  Establishment_Year__c: d.establishment_year__c ?? "",
  Nature_of_Firms__c: d.nature_of_frims__c ?? "",
  other_nature_of_Firms__c: d.other_nature_of_firms__c ?? "",
  Business_Type__c: d.type_of_business__c ?? "",
  Geo_Location__Latitude__s: d.geo_location__latitude__s ?? "",
  Geo_Location__Longitude__s: d.geo_location__longitude__s ?? "",
  // z-users passthrough (not edited)
  z1_user__c: d.z1_user__c ?? "",
  z2_user__c: d.z2_user__c ?? "",
  z3_user__c: d.z3_user__c ?? "",
  z4_user__c: d.z4_user__c ?? "",
  z5_user__c: d.z5_user__c ?? "",
  z6_user__c: d.z6_user__c ?? "",
  z7_user__c: d.z7_user__c ?? "",
  z8_user__c: d.z8_user__c ?? "",
  // page 3
  Shop_Office__c: d.shop_office__c ?? "",
  Godown_Area__c: d.area_of_godown_sq_feet__c ?? "",
  Godown_Facility__c: d.godown_facility__c ?? "",
  Total_Employees__c: d.numberofemployees ?? d.total_employees__c ?? "",
  Total_Vehicles__c: d.no_of_vehicles__c ?? "",
  GSTIN_applied__c: d.gstin_applied__c === true ? "Yes" : "No",
  GSTIN_no__c: d.gstin_no__c ?? "",
  GSTIN_Registration_Date__c: d.gstin_registration_date__c ?? null,
  INST_LIC_NO__c: d.inst_lic_no__c ?? "",
  INST_LIC_Registration_Date__c:
    d.insecticides_lic_no_registration_date__c ?? null,
  INST_LIC_Registration_Validity__c:
    d.insecticides_lic_no_valid_upto__c ?? null,
  FERT_LIC_NO__c: d.fert_lic_no__c ?? "",
  FERT_LIC_Registration_Date__c:
    d.fertilizer_lic_no_registration_date__c ?? null,
  FERT_LIC_Registration_Validity__c: d.fertilizer_lic_no_valid_upto__c ?? null,
  // page 4
  DD_Cheque_Online_Ref_No__c: d.dd_cheque_online_ref_no__c ?? "",
  Date__c: d.date__c ?? null,
  Bank_Name__c: d.bank_name__c ?? "",
  Amount__c: d.amount_inr__c ?? "",
  Bankers_Name__c: d.banker_s_name__c ?? "",
  Bankers_Address__c: d.banker_addresss__c ?? "",
  Cash_Credit_Limit__c: d.credit_limit__c ?? "",
  Deposit_Others__c: d.others_security_deposite_detai__c ?? "",
  // page 5
  Pesticides__c: d.pesticide_turnover__c ?? "",
  Fertilizers__c: d.fertilizers_turnover__c ?? "",
  Seeds__c: d.seeds_other_turnover__c ?? "",
  Product_Others__c: d.others_product_turnover__c ?? "",
  Is_Income_Tax_Assessee: d.is_your_firm_income_tax_assessee__c ?? "",
  PAN_No__c: d.pan_no__c ?? "",
  Total_Annual_Turnover__c: d.total_annual_turnover_in_lacs_iil__c ?? "",
  Capital_Employed__c: d.capital_employed__c ?? "",
  Annual_Income__c: d.annual_income__c ?? "",
  Expected_IIL_Investment__c: d.expected_investment_in_iil__c ?? "",
  Expected_1_Year_Turnover_With_IIL_c:
    d.expected_turnover_in_1st_year_with_iil__c ?? "",
  Expected_Next_Year_Turnover_With_IIL_c:
    d.expected_turnover_in_next_year_with_iil__c ?? "",
  Proposed_Payment_Terms: d.proposed_payment_terms_in_days__c ?? "",
  Payment_Credit_Limit__c: d.credit_limit_required_specify_amount_in__c ?? "",
  Total_Retailers__c: d.total_no_of_retailers__c ?? "",
  Total_Villages_Covered__c: d.no_of_village_covered__c ?? "",
  Area_Specification__c: d.specify_area__c ?? "",
  // page 7
  Dealer_Credit__c: d.rs_in_lacs__c ?? "",
  Dealer_Credit_Days__c: d.no_of_credit_days__c ?? "",
  Transporter_Name_1__c: d.name_of_transporter_1__c ?? "",
  Transporter_Name_2__c: d.name_of_transporter_2__c ?? "",
  Transporter_Name_3__c: d.name_of_transporter_3__c ?? "",
  Depot_Distance__c: d.distance_from_depot__c ?? "",
  Other_Dealer_Info__c: d.any_other_detail_info_about__c ?? "",
  Is_Party_Visited_Personally__c:
    d.have_you_visited_the_party_personally__c ?? "",
  Party_Credibility__c: d.credibility_of_party__c ?? "",
});

// -------------------------------------------------------------------------

// Fields that become read-only once the distributor already has a SAP ID
// (mirrors the Flutter app's `isHasSapId` gating). Keyed by the wizard's form
// keys. Pages 2, 4, 6, 7, 8 are not gated.
const SAP_LOCKED_KEYS = new Set([
  // page 1
  "Firm_Name__c",
  "Address__c",
  "address_2__c",
  "address_3__c",
  "State__c",
  "District__c",
  "Sub_District__c",
  "Village__c",
  "Pincode__c",
  "City__c",
  "Telephone__c",
  "Mobile__c",
  "Email__c",
  // page 3
  "GSTIN_applied__c",
  "GSTIN_no__c",
  "INST_LIC_NO__c",
  "FERT_LIC_NO__c",
  // page 5
  "PAN_No__c",
]);

const DistributorEditWizard = ({
  open,
  record,
  onClose,
  onSuccess,
  onSaved,
}) => {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  // Whether the loaded record already has a SAP ID; locks the fields above.
  const [hasSapId, setHasSapId] = useState(false);

  // Snapshot of each page's data as loaded, to detect real changes on Next.
  const initialRef = useRef({ form: {}, arrays: {} });

  const [form, setForm] = useState({});
  const [proprietors, setProprietors] = useState([]);
  const [companyDetails, setCompanyDetails] = useState([]);
  const [majorCrops, setMajorCrops] = useState([]);
  const [districtsCovered, setDistrictsCovered] = useState([]);
  const [productOfInterest, setProductOfInterest] = useState([]);
  const [sisterCompany, setSisterCompany] = useState([]);
  const [materialDispatch, setMaterialDispatch] = useState([]);
  const [files, setFiles] = useState([]);
  // Index of the document row currently uploading a replacement (-1 = none).
  const [uploadingIdx, setUploadingIdx] = useState(-1);
  // In-dialog lightbox preview: { url, fileName, kind } or null when closed.
  const [preview, setPreview] = useState(null);
  // Object URL of the fetched file, used so PDFs render inline even when the
  // host serves them with Content-Disposition: attachment / frame blocking.
  const [previewBlobUrl, setPreviewBlobUrl] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(false);

  // geo
  const [stateOptions, setStateOptions] = useState([]);
  const [districtOptions, setDistrictOptions] = useState([]);
  const [subDistrictOptions, setSubDistrictOptions] = useState([]);
  const [villageOptions, setVillageOptions] = useState([]);
  const [geoSel, setGeoSel] = useState({
    state: null,
    district: null,
    subDistrict: null,
    village: null,
  });

  // Detail-load identity. The backend's getDistributorMappingById guards with
  // `if (!herokuId || !herokuSfid) return MISSINGPARAMS`, so BOTH must be
  // truthy or the form opens blank. On the "Pending Approvals" tab records have
  // a sfid; on the "Distributor Mapping List" tab some records are SAP/heroku-
  // only with sfid === null, which made herokuId null and blanked the form.
  // getDistributorById matches `heroku_id__c = x OR sfid = x`, so fall back to
  // heroku_id__c when sfid is missing (no change when sfid exists).
  const herokuSfid = record?.heroku_id__c ?? record?.sfid;
  const herokuId = record?.sfid ?? record?.heroku_id__c;

  // Auto-capture the device location when the wizard opens. Used to fill the
  // (numeric) geo columns so the page-1 update doesn't crash on empty values.
  const { coords } = useGeolocated({
    positionOptions: { enableHighAccuracy: true },
    userDecisionTimeout: 10000,
  });

  const set = (key, value) => setForm((p) => ({ ...p, [key]: value }));

  useEffect(() => {
    if (!open || !record) return;
    loadDetail();
    loadStates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // When the lightbox opens, fetch the file and expose it as an object URL so
  // PDFs render inline regardless of the host's Content-Disposition / framing
  // headers. Images render fine from their direct URL, so skip the fetch there.
  useEffect(() => {
    let revoked = "";
    let cancelled = false;

    setPreviewError(false);
    setPreviewBlobUrl("");

    if (!preview?.url || preview.kind !== "pdf") {
      setPreviewLoading(false);
      return undefined;
    }

    setPreviewLoading(true);
    (async () => {
      try {
        const res = await fetch(preview.url);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const raw = await res.blob();
        // Always re-type as application/pdf. The host may serve the file as
        // application/octet-stream (forced download); an iframe pointing at an
        // octet-stream blob URL downloads instead of rendering, so override it.
        const pdfBlob = new Blob([raw], { type: "application/pdf" });
        const objUrl = URL.createObjectURL(pdfBlob);
        if (cancelled) {
          URL.revokeObjectURL(objUrl);
          return;
        }
        revoked = objUrl;
        setPreviewBlobUrl(objUrl);
      } catch (e) {
        console.error("PDF preview fetch failed", e);
        if (!cancelled) setPreviewError(true);
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      if (revoked) URL.revokeObjectURL(revoked);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preview]);

  const loadStates = async () => {
    try {
      const res = await window.Platform.database.getStates();
      setStateOptions(res?.data || []);
    } catch (e) {
      console.error(e);
    }
  };

  // Preload the full district/sub-district/village lists for the record's
  // current geo, so every dropdown shows all options (not just the saved one)
  // as soon as the wizard opens.
  const preloadGeoOptions = async (d) => {
    try {
      if (d.state__c) {
        const r = await window.Platform.database.getAllDistricts({
          stateSfid: d.state__c,
        });
        setDistrictOptions(r?.data || []);
      }
      if (d.district__c) {
        const r = await window.Platform.database.getAllTehsils({
          districtSfid: d.district__c,
        });
        setSubDistrictOptions(r?.data || []);
      }
      if (d.sub_district__c) {
        const r = await window.Platform.database.getVillages({
          subDistrictSfid: d.sub_district__c,
        });
        setVillageOptions(r?.data || []);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const loadDetail = async () => {
    try {
      setLoading(true);
      const result =
        await window.Platform.database.getDistributorMappingDetailsById({
          herokuId,
          herokuSfid,
        });
      const d = result?.data || {};
      // A present SAP ID locks the core identity fields (see SAP_LOCKED_KEYS).
      setHasSapId(Boolean(d.sap_id__c));
      const formData = mapRecordToForm(d);
      setForm(formData);

      // seed geo selections (id + label) so Autocomplete shows current value
      setGeoSel({
        state: d.state__c
          ? { __id: d.state__c, __label: d.state_name__c }
          : null,
        district: d.district__c
          ? { __id: d.district__c, __label: d.district_name__c }
          : null,
        subDistrict: d.sub_district__c
          ? { __id: d.sub_district__c, __label: d.sub_district_name__c }
          : null,
        village: d.village__c
          ? { __id: d.village__c, __label: d.village_name__c }
          : null,
      });

      // preload the full district/sub-district/village option lists for the
      // record's current geo so all dropdowns show every option on open.
      preloadGeoOptions(d);

      const proprietorsData = (d.Proprietor_Details__c || []).map((i) => ({
        Business_Owner_Name__c: i.name ?? "",
        Permanent_Address__c: i.permanent_residence_address__c ?? "",
        Present_Address__c: i.present_residence_address__c ?? "",
        Father_Husband_Name__c: i.father_s_husband_s_name__c ?? "",
        Business_Owner_Mobile__c: i.mobile_number__c ?? "",
        Business_Owner_Email__c: i.email_id__c ?? "",
      }));

      const companyData = (d.Company_Details__c || []).map((i) => ({
        Company_Existing_Business__c:
          i.name_of_company_doing_existing_business__c ?? "",
        Total_Dealing_Years__c: i.no_of_years_of_dealing__c ?? "",
        Major_Product__c: i.major_products__c ?? "",
        Annual_Turnover__c: i.annual_turnover__c ?? "",
        Total_Dealers__c: i.no_of_dealers__c ?? "",
        Total_Credit_Years__c: i.no_of_credit_days__c ?? "",
        Cash_Or_Credit__c: i.cash_credit__c ?? "",
      }));

      const cropsData = (d.Major_Crops__c || []).map((i) => ({
        crop_Name__c: i.crop_name__c ?? "",
      }));

      const districtsData = (d.Total_Districts_Covered__c || []).map((i) => ({
        districtSfid: i.district__c ?? i.districtsfid ?? i.sfid ?? "",
        districtName: i.district_name__c ?? i.districtname ?? "",
      }));

      const productData = (d.Product_Of_Interest__c || []).map((i) => ({
        Product_Name__c: i.product_name__c ?? "",
        Other_Products__c: i.others__c ?? "",
        Products_Unit__c: i.unit__c ?? "",
        Current_Fiscal_Year__c: i.current_fiscal_year_quantity__c ?? "",
        Next_Fiscal_Year__c: i.next_fiscal_year__c ?? "",
        Next_Fiscal_Year_Unit__c: i.next_fiscal_year_unit__c ?? "",
        Next_Fiscal_Year_Quantity__c: i.next_fiscal_year_quantity__c ?? "",
        Quantity__c: i.quantity__c ?? "",
      }));

      const sisterData = (d.Sister_Company_Details__c || []).map((i) => ({
        Sister_Company__c: i.name ?? "",
        Sister_Company_Address__c: i.sister_company_address__c ?? "",
        Sister_Company_Turnover__c: i.sister_company_turnover__c ?? "",
      }));

      const dispatchData = (d.Material_Dispatch_Destination__c || []).map(
        (i) => ({
          depot_name__c: i.depot_name__c ?? "",
        }),
      );

      // Document rows come from salesforce.file__c: name__c is the document
      // name and picture__c holds the file URL. (The old mapping looked for
      // url__c/file_url__c/url, which don't exist, so nothing was viewable.)
      const filesData = (d.Files__c || []).map((i) => ({
        fileName: i.name__c ?? i.file_name__c ?? "",
        url: i.picture__c ?? i.url__c ?? i.file_url__c ?? i.url ?? "",
        existing: true,
      }));

      setProprietors(proprietorsData);
      setCompanyDetails(companyData);
      setMajorCrops(cropsData);
      setDistrictsCovered(districtsData);
      setProductOfInterest(productData);
      setSisterCompany(sisterData);
      setMaterialDispatch(dispatchData);
      setFiles(filesData);

      // baseline snapshot for change detection
      initialRef.current = {
        form: { ...formData },
        arrays: {
          Proprietor_Details__c: proprietorsData,
          Company_Details__c: companyData,
          Major_Crops__c: cropsData,
          Total_Districts_Covered__c: districtsData,
          Product_Of_Interest__c: productData,
          Sister_Company_Details__c: sisterData,
          Material_Dispatch_Destination__c: dispatchData,
          Files__c: filesData,
        },
      };
    } catch (e) {
      console.error(e);
      notify("Failed to load distributor details", true);
    } finally {
      setLoading(false);
    }
  };

  // geo cascade handlers
  const onStateChange = async (val) => {
    setGeoSel((p) => ({
      ...p,
      state: val,
      district: null,
      subDistrict: null,
      village: null,
    }));
    set("State__c", optId(val));
    setDistrictOptions([]);
    setSubDistrictOptions([]);
    setVillageOptions([]);
    setForm((p) => ({
      ...p,
      District__c: "",
      Sub_District__c: "",
      Village__c: "",
    }));
    if (optId(val)) {
      try {
        const res = await window.Platform.database.getAllDistricts({
          stateSfid: optId(val),
        });
        setDistrictOptions(res?.data || []);
      } catch (e) {
        console.error(e);
      }
    }
  };

  const onDistrictChange = async (val) => {
    setGeoSel((p) => ({
      ...p,
      district: val,
      subDistrict: null,
      village: null,
    }));
    set("District__c", optId(val));
    setSubDistrictOptions([]);
    setVillageOptions([]);
    setForm((p) => ({ ...p, Sub_District__c: "", Village__c: "" }));
    if (optId(val)) {
      try {
        const res = await window.Platform.database.getAllTehsils({
          districtSfid: optId(val),
        });
        setSubDistrictOptions(res?.data || []);
      } catch (e) {
        console.error(e);
      }
    }
  };

  const onSubDistrictChange = async (val) => {
    setGeoSel((p) => ({ ...p, subDistrict: val, village: null }));
    set("Sub_District__c", optId(val));
    setVillageOptions([]);
    setForm((p) => ({ ...p, Village__c: "" }));
    if (optId(val)) {
      try {
        const res = await window.Platform.database.getVillages({
          subDistrictSfid: optId(val),
        });
        setVillageOptions(res?.data || []);
      } catch (e) {
        console.error(e);
      }
    }
  };

  const onVillageChange = (val) => {
    setGeoSel((p) => ({ ...p, village: val }));
    set("Village__c", optId(val));
  };

  // array helpers
  const addRow = (setter, row) => setter((p) => [...p, row]);
  const removeRow = (setter, idx) =>
    setter((p) => p.filter((_, i) => i !== idx));
  const editRow = (setter, idx, key, value) =>
    setter((p) => p.map((r, i) => (i === idx ? { ...r, [key]: value } : r)));

  // Geo lat/long are NUMERIC columns; the page-1 UPDATE rewrites them every
  // time. An empty string ('') becomes `=''` and 500s, so always send a number
  // (the record's value when we have it, otherwise 0).
  const geoVal = (v) => {
    const n = Number(v);
    return v !== "" && v !== null && v !== undefined && !Number.isNaN(n)
      ? n
      : 0;
  };

  const buildPayload = () => ({
    ...form,
    // Prefer the live device location; fall back to the record value, then 0.
    Geo_Location__Latitude__s:
      coords?.latitude ?? geoVal(form.Geo_Location__Latitude__s),
    Geo_Location__Longitude__s:
      coords?.longitude ?? geoVal(form.Geo_Location__Longitude__s),
    // GSTIN applied is a boolean on the backend (checks `=== false`)
    GSTIN_applied__c: form.GSTIN_applied__c === "Yes",
    // ensure numeric-typed fields for backend typeof checks
    Proposed_Payment_Terms: num(form.Proposed_Payment_Terms),
    Payment_Credit_Limit__c: num(form.Payment_Credit_Limit__c),
    // computed totals
    Product_Total__c:
      num(form.Pesticides__c) +
      num(form.Fertilizers__c) +
      num(form.Seeds__c) +
      num(form.Product_Others__c),
    Deposit_Total__c:
      num(form.Cash_Credit_Limit__c) + num(form.Deposit_Others__c),
    Deposit_Others__c: form.Deposit_Others__c ?? "",
    // dates -> string
    GSTIN_Registration_Date__c: fromDate(form.GSTIN_Registration_Date__c),
    INST_LIC_Registration_Date__c: fromDate(form.INST_LIC_Registration_Date__c),
    INST_LIC_Registration_Validity__c: fromDate(
      form.INST_LIC_Registration_Validity__c,
    ),
    FERT_LIC_Registration_Date__c: fromDate(form.FERT_LIC_Registration_Date__c),
    FERT_LIC_Registration_Validity__c: fromDate(
      form.FERT_LIC_Registration_Validity__c,
    ),
    Date__c: fromDate(form.Date__c),
    // arrays
    Proprietor_Details__c: proprietors,
    Company_Details__c: companyDetails,
    Major_Crops__c: majorCrops,
    Total_Districts_Covered__c: districtsCovered,
    Product_Of_Interest__c: productOfInterest,
    Sister_Company_Details__c: sisterCompany,
    Material_Dispatch_Destination__c: materialDispatch,
    // Documents: send the full current set (backend page-8 does delete+insert).
    // Only page 8 actually sends this (see PAGE_FIELDS).
    Files__c: files
      .filter((f) => f.fileName && f.url)
      .map((f) => ({ fileName: f.fileName, url: f.url })),
    // control
    herokuId,
    sfid: herokuSfid,
    isEdit: true,
    isManager: null,
    Comments__c: "",
  });

  // current live value of a page (scalars + arrays) for change detection
  const currentArrays = {
    Proprietor_Details__c: proprietors,
    Company_Details__c: companyDetails,
    Major_Crops__c: majorCrops,
    Total_Districts_Covered__c: districtsCovered,
    Product_Of_Interest__c: productOfInterest,
    Sister_Company_Details__c: sisterCompany,
    Material_Dispatch_Destination__c: materialDispatch,
    Files__c: files,
  };

  const normScalar = (v) => {
    if (v === null || v === undefined) return "";
    if (dayjs.isDayjs(v)) return v.isValid() ? v.format("YYYY-MM-DD") : "";
    return String(v);
  };

  const isPageDirty = (pageNumber) => {
    const cfg = PAGE_FIELDS[pageNumber];
    if (!cfg) return false;
    const initForm = initialRef.current.form || {};
    for (const key of cfg.scalars) {
      if (normScalar(form[key]) !== normScalar(initForm[key])) return true;
    }
    for (const arrName of cfg.arrays) {
      const initArr = initialRef.current.arrays?.[arrName] || [];
      if (
        JSON.stringify(currentArrays[arrName] || []) !== JSON.stringify(initArr)
      ) {
        return true;
      }
    }
    return false;
  };

  // sync the baseline for a page after a successful save
  const commitSnapshot = (pageNumber) => {
    const cfg = PAGE_FIELDS[pageNumber];
    if (!cfg) return;
    cfg.scalars.forEach((key) => {
      initialRef.current.form[key] = form[key];
    });
    cfg.arrays.forEach((arrName) => {
      initialRef.current.arrays[arrName] = JSON.parse(
        JSON.stringify(currentArrays[arrName] || []),
      );
    });
  };

  // Build a payload containing ONLY the given page's fields (+ control keys),
  // so each Next sends that page's data — not the whole form.
  const buildPagePayload = (pageNumber) => {
    const full = buildPayload();
    const cfg = PAGE_FIELDS[pageNumber] || { scalars: [], arrays: [] };
    const extra = PAGE_EXTRA_SEND[pageNumber] || [];
    const payload = {
      // The page-1 UPDATE and all child delete/insert queries key off
      // account.heroku_id__c, NOT the SF sfid. If the record already has a
      // heroku id, send it so the UPDATE matches. If it doesn't (SAP-synced
      // account), send herokuId='' + the sfid so the backend stamps a heroku
      // id onto the account (see the `herokuId===''` branch) before updating.
      herokuId: record?.heroku_id__c || "",
      sfid: record?.sfid,
      pageNumber,
      isEdit: true,
      isManager: null,
      isFinalSubmit: false, // never final -> status is never changed
      Comments__c: "",
    };
    [...cfg.scalars, ...extra].forEach((k) => {
      payload[k] = full[k];
    });
    (cfg.arrays || []).forEach((k) => {
      payload[k] = full[k];
    });
    return payload;
  };

  // Fields shown on the distributor list card, keyed by the list/MV column
  // names, built from the current form. Sent to the parent after a save so it
  // can optimistically patch the list row (the list's materialized view only
  // refreshes on a ~10-min cron).
  const buildCardPatch = () => ({
    firm_name__c: form.Firm_Name__c ?? "",
    city__c: form.City__c ?? "",
    mobile__c: form.Mobile__c ?? "",
    pincode__c: form.Pincode__c ?? "",
    gstin_no__c: form.GSTIN_no__c ?? "",
    gstin_applied__c: form.GSTIN_applied__c === "Yes",
    sub_district_name__c: optLabel(geoSel.subDistrict) || "",
  });

  // Upload a replacement file for a document row, then swap its URL in place
  // (keeping the same document name). Approval status is untouched on save.
  const replaceDocument = async (idx, file) => {
    if (!file) return;
    try {
      setUploadingIdx(idx);
      const fd = new FormData();
      fd.append("image", file);
      const res = await window.Platform.database.uploadFile(fd);
      if (!res || res.hasError || !res.data) {
        throw new Error(res?.message || "Upload failed");
      }
      editRow(setFiles, idx, "url", res.data);
    } catch (e) {
      console.error(e);
      notify(e?.message || "Failed to upload the document", true);
    } finally {
      setUploadingIdx(-1);
    }
  };

  // Save one page to the DB via the page-wise API. Returns true on success.
  const savePage = async (pageNumber) => {
    try {
      const res =
        await window.Platform.database.addDistributorsMappingDetailsPageWise(
          buildPagePayload(pageNumber),
        );
      if (!res || res.hasError) {
        notify(
          `${STEPS[pageNumber - 1]}: ${res?.message || "Save failed"}`,
          true,
        );
        return false;
      }
      commitSnapshot(pageNumber);
      onSaved && onSaved(buildCardPatch());
      return true;
    } catch (e) {
      console.error(e);
      notify(
        `${STEPS[pageNumber - 1]}: ${e?.message || "Save failed on the server"}`,
        true,
      );
      return false;
    }
  };

  // Next: if the current page changed, save it before advancing.
  const handleNext = async () => {
    const pageNumber = activeStep + 1;
    if (isPageDirty(pageNumber)) {
      setSubmitting(true);
      const ok = await savePage(pageNumber);
      setSubmitting(false);
      if (!ok) return;
      notify(`${STEPS[pageNumber - 1]} saved`, false);
    }
    setActiveStep((s) => s + 1);
  };

  // Done: save the last visible page if it changed, then show a success popup
  // and close/refresh.
  const handleFinish = async () => {
    const pageNumber = activeStep + 1;
    if (isPageDirty(pageNumber)) {
      setSubmitting(true);
      const ok = await savePage(pageNumber);
      setSubmitting(false);
      if (!ok) return;
    }
    await Swal.fire({
      icon: "success",
      title: "Saved successfully",
      // Attachments are re-written on the backend and can take a few seconds
      // to appear; let the user know so an immediate re-open isn't confusing.
      text: "Your changes have been saved. Updated attachments may take a few seconds to reflect.",
      confirmButtonColor: "#3085d6",
      confirmButtonText: "OK",
    });
    onSuccess && onSuccess();
  };

  // A field is locked when the record has a SAP ID and the key is in the SAP
  // lock set. GSTIN No. is additionally locked whenever GSTIN Applied is 'Yes'
  // (regardless of SAP ID), matching the mobile app.
  const isLocked = (key) => {
    if (key === "GSTIN_no__c" && form.GSTIN_applied__c === "Yes") return true;
    return hasSapId && SAP_LOCKED_KEYS.has(key);
  };

  const tf = (label, key, opts = {}) => (
    <TextField
      fullWidth
      size="small"
      label={label}
      value={form[key] ?? ""}
      onChange={(e) => set(key, e.target.value)}
      type={opts.type || "text"}
      multiline={opts.multiline}
      minRows={opts.multiline ? 2 : undefined}
      required={opts.required}
      disabled={opts.disabled ?? isLocked(key)}
    />
  );

  const selYesNo = (label, key, opts = {}) => (
    <TextField
      select
      fullWidth
      size="small"
      label={label}
      value={form[key] ?? ""}
      onChange={(e) => set(key, e.target.value)}
      required={opts.required}
      disabled={opts.disabled ?? isLocked(key)}
    >
      {YES_NO.map((o) => (
        <MenuItem key={o} value={o}>
          {o}
        </MenuItem>
      ))}
    </TextField>
  );

  const dateField = (label, key, opts = {}) => (
    <DatePicker
      label={label}
      value={toDate(form[key])}
      onChange={(d) => set(key, d)}
      slotProps={{
        textField: { size: "small", fullWidth: true, required: opts.required },
      }}
    />
  );

  const geoAuto = (label, options, value, onChange, disabled = false) => (
    <Autocomplete
      size="small"
      disabled={disabled}
      options={options}
      value={value}
      onChange={(_, v) => onChange(v)}
      getOptionLabel={(o) => optLabel(o) || ""}
      isOptionEqualToValue={(o, v) => optId(o) === optId(v)}
      renderInput={(params) => <TextField {...params} label={label} />}
    />
  );

  const SectionTitle = ({ children }) => (
    <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 1, mb: 0.5 }}>
      {children}
    </Typography>
  );

  const renderStep = () => {
    switch (activeStep) {
      case 0:
        return (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary">
                {coords
                  ? `Location captured: ${coords.latitude.toFixed(5)}, ${coords.longitude.toFixed(5)}`
                  : "Capturing current location… (allow the browser permission prompt)"}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              {tf("Firm Name", "Firm_Name__c", { required: true })}
            </Grid>
            <Grid item xs={12} sm={6}>
              {tf("Address", "Address__c", { required: true })}
            </Grid>
            <Grid item xs={12} sm={6}>
              {tf("Address Line 2", "address_2__c")}
            </Grid>
            <Grid item xs={12} sm={6}>
              {tf("Address Line 3", "address_3__c")}
            </Grid>
            <Grid item xs={12} sm={6}>
              {geoAuto(
                "State",
                stateOptions,
                geoSel.state,
                onStateChange,
                hasSapId,
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              {geoAuto(
                "District",
                districtOptions,
                geoSel.district,
                onDistrictChange,
                hasSapId,
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              {geoAuto(
                "Sub District",
                subDistrictOptions,
                geoSel.subDistrict,
                onSubDistrictChange,
                hasSapId,
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              {geoAuto(
                "Village",
                villageOptions,
                geoSel.village,
                onVillageChange,
                hasSapId,
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              {tf("City", "City__c", { required: true })}
            </Grid>
            <Grid item xs={12} sm={6}>
              {tf("Pincode", "Pincode__c", { required: true })}
            </Grid>
            <Grid item xs={12} sm={6}>
              {tf("Telephone", "Telephone__c")}
            </Grid>
            <Grid item xs={12} sm={6}>
              {tf("Mobile", "Mobile__c", { required: true })}
            </Grid>
            <Grid item xs={12} sm={6}>
              {selYesNo("Mobile linked with Aadhaar?", "Is_Mobile_Linked__c", {
                required: true,
              })}
            </Grid>
            <Grid item xs={12} sm={6}>
              {selYesNo("Is WhatsApp number?", "Is_Mobile_Whatsapp__c", {
                required: true,
              })}
            </Grid>
            <Grid item xs={12} sm={6}>
              {tf("Alternate Mobile", "Alternate_Mobile__c")}
            </Grid>
            <Grid item xs={12} sm={6}>
              {tf("Email", "Email__c")}
            </Grid>
            <Grid item xs={12} sm={6}>
              {tf("Establishment Year", "Establishment_Year__c", {
                required: true,
              })}
            </Grid>
            <Grid item xs={12} sm={6}>
              {tf("Nature of Firm", "Nature_of_Firms__c", { required: true })}
            </Grid>
            <Grid item xs={12} sm={6}>
              {tf("Other Nature of Firm", "other_nature_of_Firms__c")}
            </Grid>
            <Grid item xs={12} sm={6}>
              {tf("Business Type", "Business_Type__c", { required: true })}
            </Grid>
          </Grid>
        );
      case 1:
        return (
          <Box>
            {proprietors.map((row, idx) => (
              <Box
                key={idx}
                sx={{ border: "1px solid #eee", borderRadius: 2, p: 2, mb: 2 }}
              >
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <SectionTitle>Proprietor {idx + 1}</SectionTitle>
                  <IconButton
                    size="small"
                    onClick={() => removeRow(setProprietors, idx)}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Name"
                      value={row.Business_Owner_Name__c}
                      onChange={(e) =>
                        editRow(
                          setProprietors,
                          idx,
                          "Business_Owner_Name__c",
                          e.target.value,
                        )
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Father/Husband Name"
                      value={row.Father_Husband_Name__c}
                      onChange={(e) =>
                        editRow(
                          setProprietors,
                          idx,
                          "Father_Husband_Name__c",
                          e.target.value,
                        )
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Permanent Address"
                      value={row.Permanent_Address__c}
                      onChange={(e) =>
                        editRow(
                          setProprietors,
                          idx,
                          "Permanent_Address__c",
                          e.target.value,
                        )
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Present Address"
                      value={row.Present_Address__c}
                      onChange={(e) =>
                        editRow(
                          setProprietors,
                          idx,
                          "Present_Address__c",
                          e.target.value,
                        )
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Mobile"
                      value={row.Business_Owner_Mobile__c}
                      onChange={(e) =>
                        editRow(
                          setProprietors,
                          idx,
                          "Business_Owner_Mobile__c",
                          e.target.value,
                        )
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Email"
                      value={row.Business_Owner_Email__c}
                      onChange={(e) =>
                        editRow(
                          setProprietors,
                          idx,
                          "Business_Owner_Email__c",
                          e.target.value,
                        )
                      }
                    />
                  </Grid>
                </Grid>
              </Box>
            ))}
            <Button
              startIcon={<AddIcon />}
              onClick={() =>
                addRow(setProprietors, {
                  Business_Owner_Name__c: "",
                  Permanent_Address__c: "",
                  Present_Address__c: "",
                  Father_Husband_Name__c: "",
                  Business_Owner_Mobile__c: "",
                  Business_Owner_Email__c: "",
                })
              }
            >
              Add Proprietor
            </Button>
          </Box>
        );
      case 2:
        return (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              {tf("Shop / Office", "Shop_Office__c", { required: true })}
            </Grid>
            <Grid item xs={12} sm={6}>
              {tf("Godown Area (sq ft)", "Godown_Area__c", {
                required: true,
                type: "number",
              })}
            </Grid>
            <Grid item xs={12} sm={6}>
              {tf("Godown Facility", "Godown_Facility__c", { required: true })}
            </Grid>
            <Grid item xs={12} sm={6}>
              {tf("Total Employees", "Total_Employees__c", { type: "number" })}
            </Grid>
            <Grid item xs={12} sm={6}>
              {tf("Total Vehicles", "Total_Vehicles__c", { type: "number" })}
            </Grid>
            <Grid item xs={12} sm={6}>
              {selYesNo("GSTIN Applied?", "GSTIN_applied__c")}
            </Grid>
            <Grid item xs={12} sm={6}>
              {tf("GSTIN No", "GSTIN_no__c")}
            </Grid>
            <Grid item xs={12} sm={6}>
              {dateField(
                "GSTIN Registration Date",
                "GSTIN_Registration_Date__c",
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              {tf("INST License No", "INST_LIC_NO__c", { required: true })}
            </Grid>
            <Grid item xs={12} sm={6}>
              {dateField(
                "INST License Reg. Date",
                "INST_LIC_Registration_Date__c",
                { required: true },
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              {dateField(
                "INST License Validity",
                "INST_LIC_Registration_Validity__c",
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              {tf("FERT License No", "FERT_LIC_NO__c")}
            </Grid>
            <Grid item xs={12} sm={6}>
              {dateField(
                "FERT License Reg. Date",
                "FERT_LIC_Registration_Date__c",
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              {dateField(
                "FERT License Validity",
                "FERT_LIC_Registration_Validity__c",
              )}
            </Grid>
          </Grid>
        );
      case 3:
        return (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              {tf("DD/Cheque/Online Ref No", "DD_Cheque_Online_Ref_No__c", {
                required: true,
              })}
            </Grid>
            <Grid item xs={12} sm={6}>
              {dateField("Date", "Date__c", { required: true })}
            </Grid>
            <Grid item xs={12} sm={6}>
              {tf("Bank Name", "Bank_Name__c", { required: true })}
            </Grid>
            <Grid item xs={12} sm={6}>
              {tf("Amount (INR)", "Amount__c", { type: "number" })}
            </Grid>
            <Grid item xs={12} sm={6}>
              {tf("Banker's Name", "Bankers_Name__c")}
            </Grid>
            <Grid item xs={12} sm={6}>
              {tf("Banker's Address", "Bankers_Address__c", { required: true })}
            </Grid>
            <Grid item xs={12} sm={6}>
              {tf("Cash Credit Limit", "Cash_Credit_Limit__c", {
                type: "number",
              })}
            </Grid>
            <Grid item xs={12} sm={6}>
              {tf("Other Security Deposit", "Deposit_Others__c")}
            </Grid>
          </Grid>
        );
      case 4:
        return (
          <Box>
            <SectionTitle>Turnover (in lacs)</SectionTitle>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                {tf("Pesticides Turnover", "Pesticides__c", {
                  required: true,
                  type: "number",
                })}
              </Grid>
              <Grid item xs={12} sm={6}>
                {tf("Fertilizers Turnover", "Fertilizers__c", {
                  type: "number",
                })}
              </Grid>
              <Grid item xs={12} sm={6}>
                {tf("Seeds/Other Turnover", "Seeds__c", { type: "number" })}
              </Grid>
              <Grid item xs={12} sm={6}>
                {tf("Other Product Turnover", "Product_Others__c", {
                  type: "number",
                })}
              </Grid>
              <Grid item xs={12} sm={6}>
                {selYesNo("Income Tax Assessee?", "Is_Income_Tax_Assessee", {
                  required: true,
                })}
              </Grid>
              <Grid item xs={12} sm={6}>
                {tf("PAN No", "PAN_No__c", { required: true })}
              </Grid>
              <Grid item xs={12} sm={6}>
                {tf("Total Annual Turnover", "Total_Annual_Turnover__c", {
                  type: "number",
                })}
              </Grid>
              <Grid item xs={12} sm={6}>
                {tf("Capital Employed", "Capital_Employed__c", {
                  type: "number",
                })}
              </Grid>
              <Grid item xs={12} sm={6}>
                {tf("Annual Income", "Annual_Income__c", { type: "number" })}
              </Grid>
              <Grid item xs={12} sm={6}>
                {tf("Expected IIL Investment", "Expected_IIL_Investment__c", {
                  type: "number",
                })}
              </Grid>
              <Grid item xs={12} sm={6}>
                {tf(
                  "Expected 1st Year Turnover with IIL",
                  "Expected_1_Year_Turnover_With_IIL_c",
                  { required: true, type: "number" },
                )}
              </Grid>
              <Grid item xs={12} sm={6}>
                {tf(
                  "Expected Next Year Turnover with IIL",
                  "Expected_Next_Year_Turnover_With_IIL_c",
                  { type: "number" },
                )}
              </Grid>
              <Grid item xs={12} sm={6}>
                {tf("Proposed Payment Terms (days)", "Proposed_Payment_Terms", {
                  required: true,
                  type: "number",
                })}
              </Grid>
              <Grid item xs={12} sm={6}>
                {tf("Payment Credit Limit", "Payment_Credit_Limit__c", {
                  required: true,
                  type: "number",
                })}
              </Grid>
              <Grid item xs={12} sm={6}>
                {tf("Total Retailers", "Total_Retailers__c", {
                  required: true,
                  type: "number",
                })}
              </Grid>
              <Grid item xs={12} sm={6}>
                {tf("Villages Covered", "Total_Villages_Covered__c", {
                  type: "number",
                })}
              </Grid>
              <Grid item xs={12} sm={6}>
                {tf("Area Specification", "Area_Specification__c")}
              </Grid>
            </Grid>

            <SectionTitle>Districts Covered (required)</SectionTitle>
            {districtsCovered.map((row, idx) => (
              <Box key={idx} display="flex" gap={1} alignItems="center" mb={1}>
                <TextField
                  size="small"
                  label="District Name"
                  value={row.districtName}
                  onChange={(e) =>
                    editRow(
                      setDistrictsCovered,
                      idx,
                      "districtName",
                      e.target.value,
                    )
                  }
                />
                <TextField
                  size="small"
                  label="District Sfid"
                  value={row.districtSfid}
                  onChange={(e) =>
                    editRow(
                      setDistrictsCovered,
                      idx,
                      "districtSfid",
                      e.target.value,
                    )
                  }
                  sx={{ flex: 1 }}
                />
                <IconButton
                  size="small"
                  onClick={() => removeRow(setDistrictsCovered, idx)}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
            <Button
              startIcon={<AddIcon />}
              onClick={() =>
                addRow(setDistrictsCovered, {
                  districtSfid: "",
                  districtName: "",
                })
              }
            >
              Add District
            </Button>

            <SectionTitle>Existing Company Details (required)</SectionTitle>
            {companyDetails.map((row, idx) => (
              <Box
                key={idx}
                sx={{ border: "1px solid #eee", borderRadius: 2, p: 2, mb: 2 }}
              >
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="caption">Company {idx + 1}</Typography>
                  <IconButton
                    size="small"
                    onClick={() => removeRow(setCompanyDetails, idx)}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Company (existing business)"
                      value={row.Company_Existing_Business__c}
                      onChange={(e) =>
                        editRow(
                          setCompanyDetails,
                          idx,
                          "Company_Existing_Business__c",
                          e.target.value,
                        )
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Years of Dealing"
                      type="number"
                      value={row.Total_Dealing_Years__c}
                      onChange={(e) =>
                        editRow(
                          setCompanyDetails,
                          idx,
                          "Total_Dealing_Years__c",
                          e.target.value,
                        )
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Major Product"
                      value={row.Major_Product__c}
                      onChange={(e) =>
                        editRow(
                          setCompanyDetails,
                          idx,
                          "Major_Product__c",
                          e.target.value,
                        )
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Annual Turnover"
                      type="number"
                      value={row.Annual_Turnover__c}
                      onChange={(e) =>
                        editRow(
                          setCompanyDetails,
                          idx,
                          "Annual_Turnover__c",
                          e.target.value,
                        )
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Total Dealers"
                      type="number"
                      value={row.Total_Dealers__c}
                      onChange={(e) =>
                        editRow(
                          setCompanyDetails,
                          idx,
                          "Total_Dealers__c",
                          e.target.value,
                        )
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Credit Days"
                      type="number"
                      value={row.Total_Credit_Years__c}
                      onChange={(e) =>
                        editRow(
                          setCompanyDetails,
                          idx,
                          "Total_Credit_Years__c",
                          e.target.value,
                        )
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Cash / Credit"
                      value={row.Cash_Or_Credit__c}
                      onChange={(e) =>
                        editRow(
                          setCompanyDetails,
                          idx,
                          "Cash_Or_Credit__c",
                          e.target.value,
                        )
                      }
                    />
                  </Grid>
                </Grid>
              </Box>
            ))}
            <Button
              startIcon={<AddIcon />}
              onClick={() =>
                addRow(setCompanyDetails, {
                  Company_Existing_Business__c: "",
                  Total_Dealing_Years__c: "",
                  Major_Product__c: "",
                  Annual_Turnover__c: "",
                  Total_Dealers__c: "",
                  Total_Credit_Years__c: "",
                  Cash_Or_Credit__c: "",
                })
              }
            >
              Add Company
            </Button>

            <SectionTitle>Major Crops</SectionTitle>
            {majorCrops.map((row, idx) => (
              <Box key={idx} display="flex" gap={1} alignItems="center" mb={1}>
                <TextField
                  size="small"
                  label="Crop Name"
                  value={row.crop_Name__c}
                  onChange={(e) =>
                    editRow(setMajorCrops, idx, "crop_Name__c", e.target.value)
                  }
                  sx={{ flex: 1 }}
                />
                <IconButton
                  size="small"
                  onClick={() => removeRow(setMajorCrops, idx)}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
            <Button
              startIcon={<AddIcon />}
              onClick={() => addRow(setMajorCrops, { crop_Name__c: "" })}
            >
              Add Crop
            </Button>
          </Box>
        );
      case 5:
        return (
          <Box>
            <SectionTitle>Product of Interest (required)</SectionTitle>
            {productOfInterest.map((row, idx) => (
              <Box
                key={idx}
                sx={{ border: "1px solid #eee", borderRadius: 2, p: 2, mb: 2 }}
              >
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="caption">Product {idx + 1}</Typography>
                  <IconButton
                    size="small"
                    onClick={() => removeRow(setProductOfInterest, idx)}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Product Name"
                      value={row.Product_Name__c}
                      onChange={(e) =>
                        editRow(
                          setProductOfInterest,
                          idx,
                          "Product_Name__c",
                          e.target.value,
                        )
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Other Products"
                      value={row.Other_Products__c}
                      onChange={(e) =>
                        editRow(
                          setProductOfInterest,
                          idx,
                          "Other_Products__c",
                          e.target.value,
                        )
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Unit"
                      value={row.Products_Unit__c}
                      onChange={(e) =>
                        editRow(
                          setProductOfInterest,
                          idx,
                          "Products_Unit__c",
                          e.target.value,
                        )
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Current FY Quantity"
                      type="number"
                      value={row.Current_Fiscal_Year__c}
                      onChange={(e) =>
                        editRow(
                          setProductOfInterest,
                          idx,
                          "Current_Fiscal_Year__c",
                          e.target.value,
                        )
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Next FY"
                      value={row.Next_Fiscal_Year__c}
                      onChange={(e) =>
                        editRow(
                          setProductOfInterest,
                          idx,
                          "Next_Fiscal_Year__c",
                          e.target.value,
                        )
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Next FY Unit"
                      value={row.Next_Fiscal_Year_Unit__c}
                      onChange={(e) =>
                        editRow(
                          setProductOfInterest,
                          idx,
                          "Next_Fiscal_Year_Unit__c",
                          e.target.value,
                        )
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Next FY Quantity"
                      type="number"
                      value={row.Next_Fiscal_Year_Quantity__c}
                      onChange={(e) =>
                        editRow(
                          setProductOfInterest,
                          idx,
                          "Next_Fiscal_Year_Quantity__c",
                          e.target.value,
                        )
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Quantity"
                      type="number"
                      value={row.Quantity__c}
                      onChange={(e) =>
                        editRow(
                          setProductOfInterest,
                          idx,
                          "Quantity__c",
                          e.target.value,
                        )
                      }
                    />
                  </Grid>
                </Grid>
              </Box>
            ))}
            <Button
              startIcon={<AddIcon />}
              onClick={() =>
                addRow(setProductOfInterest, {
                  Product_Name__c: "",
                  Other_Products__c: "",
                  Products_Unit__c: "",
                  Current_Fiscal_Year__c: "",
                  Next_Fiscal_Year__c: "",
                  Next_Fiscal_Year_Unit__c: "",
                  Next_Fiscal_Year_Quantity__c: "",
                  Quantity__c: "",
                })
              }
            >
              Add Product
            </Button>

            <SectionTitle>Sister Company Details</SectionTitle>
            {sisterCompany.map((row, idx) => (
              <Box
                key={idx}
                sx={{ border: "1px solid #eee", borderRadius: 2, p: 2, mb: 2 }}
              >
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="caption">
                    Sister Company {idx + 1}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => removeRow(setSisterCompany, idx)}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Company Name"
                      value={row.Sister_Company__c}
                      onChange={(e) =>
                        editRow(
                          setSisterCompany,
                          idx,
                          "Sister_Company__c",
                          e.target.value,
                        )
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Address"
                      value={row.Sister_Company_Address__c}
                      onChange={(e) =>
                        editRow(
                          setSisterCompany,
                          idx,
                          "Sister_Company_Address__c",
                          e.target.value,
                        )
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Turnover"
                      type="number"
                      value={row.Sister_Company_Turnover__c}
                      onChange={(e) =>
                        editRow(
                          setSisterCompany,
                          idx,
                          "Sister_Company_Turnover__c",
                          e.target.value,
                        )
                      }
                    />
                  </Grid>
                </Grid>
              </Box>
            ))}
            <Button
              startIcon={<AddIcon />}
              onClick={() =>
                addRow(setSisterCompany, {
                  Sister_Company__c: "",
                  Sister_Company_Address__c: "",
                  Sister_Company_Turnover__c: "",
                })
              }
            >
              Add Sister Company
            </Button>
          </Box>
        );
      case 6:
        return (
          <Box>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                {tf("Transporter Name 1", "Transporter_Name_1__c")}
              </Grid>
              <Grid item xs={12} sm={4}>
                {tf("Transporter Name 2", "Transporter_Name_2__c")}
              </Grid>
              <Grid item xs={12} sm={4}>
                {tf("Transporter Name 3", "Transporter_Name_3__c")}
              </Grid>
              <Grid item xs={12} sm={6}>
                {tf("Dealer Credit (Rs in lacs)", "Dealer_Credit__c", {
                  type: "number",
                })}
              </Grid>
              <Grid item xs={12} sm={6}>
                {tf("Dealer Credit Days", "Dealer_Credit_Days__c", {
                  type: "number",
                })}
              </Grid>
              <Grid item xs={12} sm={6}>
                {tf("Distance from Depot", "Depot_Distance__c", {
                  type: "number",
                })}
              </Grid>
              <Grid item xs={12} sm={6}>
                {selYesNo(
                  "Party Visited Personally?",
                  "Is_Party_Visited_Personally__c",
                )}
              </Grid>
              <Grid item xs={12} sm={6}>
                {tf("Party Credibility", "Party_Credibility__c")}
              </Grid>
              <Grid item xs={12}>
                {tf("Other Dealer Info", "Other_Dealer_Info__c", {
                  multiline: true,
                })}
              </Grid>
            </Grid>

            <SectionTitle>
              Material Dispatch Destination (required)
            </SectionTitle>
            {materialDispatch.map((row, idx) => (
              <Box key={idx} display="flex" gap={1} alignItems="center" mb={1}>
                <TextField
                  size="small"
                  label="Depot Name"
                  value={row.depot_name__c}
                  onChange={(e) =>
                    editRow(
                      setMaterialDispatch,
                      idx,
                      "depot_name__c",
                      e.target.value,
                    )
                  }
                  sx={{ flex: 1 }}
                />
                <IconButton
                  size="small"
                  onClick={() => removeRow(setMaterialDispatch, idx)}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
            <Button
              startIcon={<AddIcon />}
              onClick={() => addRow(setMaterialDispatch, { depot_name__c: "" })}
            >
              Add Destination
            </Button>
          </Box>
        );
      case 7: {
        const baselineFiles = initialRef.current.arrays?.Files__c || [];
        return (
          <Box>
            <SectionTitle>Documents</SectionTitle>
            {files.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                No documents attached.
              </Typography>
            )}
            {files.map((f, idx) => {
              const changed = (baselineFiles[idx]?.url ?? "") !== (f.url ?? "");
              const kind = fileKind(f.url, f.fileName);
              return (
                <Box
                  key={idx}
                  display="flex"
                  gap={1.5}
                  alignItems="center"
                  mb={1}
                  sx={{ borderBottom: "1px solid #f0f0f0", pb: 1 }}
                >
                  <Box
                    sx={{
                      width: 72,
                      height: 88,
                      flexShrink: 0,
                      border: "1px solid #e0e0e0",
                      borderRadius: 1,
                      overflow: "hidden",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: "#fafafa",
                    }}
                  >
                    {f.url && kind === "image" && (
                      <Box
                        component="img"
                        src={f.url}
                        alt={f.fileName || "document"}
                        sx={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    )}
                    {f.url && kind === "pdf" && (
                      <Typography
                        variant="caption"
                        sx={{ fontWeight: 700, color: "#c62828" }}
                      >
                        PDF
                      </Typography>
                    )}
                    {(!f.url || kind === "other") && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ px: 0.5, textAlign: "center" }}
                      >
                        {f.url ? "No preview" : "No file"}
                      </Typography>
                    )}
                  </Box>
                  <Typography variant="body2" sx={{ flex: 1 }}>
                    {f.fileName || "Document"}
                    {changed && (
                      <Typography
                        component="span"
                        variant="caption"
                        sx={{ ml: 1, color: "#00B200", fontWeight: 700 }}
                      >
                        (updated)
                      </Typography>
                    )}
                  </Typography>
                  {f.url ? (
                    <Button
                      size="small"
                      onClick={() =>
                        setPreview({
                          url: f.url,
                          fileName: f.fileName || "Document",
                          kind,
                        })
                      }
                    >
                      View
                    </Button>
                  ) : (
                    <Typography variant="caption" color="text.secondary">
                      No file
                    </Typography>
                  )}
                  <Button
                    size="small"
                    variant="outlined"
                    component="label"
                    disabled={uploadingIdx !== -1}
                    startIcon={
                      uploadingIdx === idx ? (
                        <CircularProgress size={14} />
                      ) : undefined
                    }
                  >
                    {uploadingIdx === idx ? "Uploading…" : "Replace"}
                    <input
                      hidden
                      type="file"
                      accept="image/*,application/pdf"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        e.target.value = "";
                        replaceDocument(idx, file);
                      }}
                    />
                  </Button>
                </Box>
              );
            })}
            <Typography
              variant="caption"
              display="block"
              sx={{ mt: 2, color: "text.secondary" }}
            >
              Use Replace to re-upload a document, then click Done to save. The
              approval status stays unchanged.
            </Typography>
          </Box>
        );
      }
      default:
        return null;
    }
  };

  return (
    <Dialog
      open={open}
      // Ignore backdrop clicks and Escape so an in-progress edit isn't lost by
      // clicking outside; the dialog only closes via the ✕ button (onClose).
      onClose={(event, reason) => {
        if (reason === "backdropClick" || reason === "escapeKeyDown") return;
        if (!submitting) onClose();
      }}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { height: "90vh" } }}
    >
      <DialogTitle>
        Edit Distributor
        <IconButton
          onClick={onClose}
          disabled={submitting}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 3 }}>
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {loading ? (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress />
          </Box>
        ) : (
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            {renderStep()}
          </LocalizationProvider>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          disabled={activeStep === 0 || submitting}
          onClick={() => setActiveStep((s) => s - 1)}
        >
          Back
        </Button>
        {activeStep < STEPS.length - 1 ? (
          <Button
            variant="contained"
            onClick={handleNext}
            disabled={submitting}
          >
            {submitting ? "Saving…" : "Next"}
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={handleFinish}
            disabled={submitting}
          >
            {submitting ? "Saving…" : "Done"}
          </Button>
        )}
      </DialogActions>

      {/* In-dialog lightbox: full preview of the selected document. */}
      <Dialog
        open={Boolean(preview)}
        onClose={() => setPreview(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { height: "90vh" } }}
      >
        <DialogTitle sx={{ pr: 6 }}>
          {preview?.fileName || "Preview"}
          <IconButton
            onClick={() => setPreview(null)}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          dividers
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "#f5f5f5",
            p: 0,
          }}
        >
          {preview?.kind === "image" && (
            <Box
              component="img"
              src={preview.url}
              alt={preview.fileName}
              sx={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
            />
          )}
          {preview?.kind === "pdf" && previewLoading && (
            <CircularProgress />
          )}
          {preview?.kind === "pdf" && !previewLoading && previewBlobUrl && (
            <Box
              component="iframe"
              src={previewBlobUrl}
              title={preview.fileName}
              sx={{ width: "100%", height: "100%", border: 0 }}
            />
          )}
          {preview?.kind === "pdf" && !previewLoading && !previewBlobUrl && (
            <Box sx={{ textAlign: "center", p: 4 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {previewError
                  ? "Couldn't load this PDF for preview."
                  : "Preparing preview…"}
              </Typography>
              <Button
                variant="contained"
                href={preview.url}
                target="_blank"
                rel="noreferrer"
              >
                Open in new tab
              </Button>
            </Box>
          )}
          {preview && preview.kind === "other" && (
            <Box sx={{ textAlign: "center", p: 4 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                This file type can't be previewed here.
              </Typography>
              <Button
                variant="contained"
                href={preview.url}
                target="_blank"
                rel="noreferrer"
              >
                Open in new tab
              </Button>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Button
            href={previewBlobUrl || preview?.url}
            target="_blank"
            rel="noreferrer"
            disabled={!preview?.url || previewLoading}
          >
            Open in new tab
          </Button>
          <Button onClick={() => setPreview(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default DistributorEditWizard;
